"use client";

import { useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router";

import { isValidCheckoutId, seedCheckoutId } from "@/lib/checkout-id";
import { isNativeInstoreHandshake } from "@/lib/instore-handshake";
import { logWarn } from "@/services/logging/logger";

import { WelcomeScreen } from "../views/welcome-screen";
import { collectHandshakeMetadata } from "../lib/collect-metadata";
import {
  buildAuthLoginUrl,
  buildInstoreCheckoutUrl,
} from "../lib/build-auth-login-url";
import { emitCheckoutHandshake } from "../analytics";
import { RIVER_EVENT, flushRiver, trackRiver } from "@/services/river";

/**
 * Invalid handshake entries still land on the existing PPUI not-found route
 * under `/v2` (handshake itself is served at site-root `/handshake`).
 */
const PLAN_SELECTION_BASENAME = "/v2";

function navigateAfterHandshake(checkoutId: string): void {
  const search = window.location.search;
  const goesToAuth = !isNativeInstoreHandshake();
  const target = goesToAuth
    ? buildAuthLoginUrl({ checkoutId, search })
    : buildInstoreCheckoutUrl({ checkoutId, search });

  // Only the browser path visits Auth. A native in-store handshake goes
  // straight to checkout, so counting it here would overstate login reach.
  if (goesToAuth) {
    trackRiver({ event: RIVER_EVENT.authRedirectTriggered, checkoutId });
  }

  // The River batcher flushes on size, a 2s timer or tab-hide. The synchronous
  // full-page navigation below beats all three, so force the queued
  // CheckoutRendered out first. keepalive stops the browser cancelling the
  // request as the page unloads.
  flushRiver({ keepalive: true });

  window.location.assign(target);
}

export function HandshakePage() {
  const params = useParams<{ checkoutId?: string }>();
  const [searchParams] = useSearchParams();
  // Path id first (/handshake/<id> survives merchants that strip the query),
  // then the ?checkoutId= query for the bare /handshake entry.
  const checkoutId =
    params.checkoutId ?? searchParams.get("checkoutId") ?? undefined;
  // One emit / one navigation per page lifetime (StrictMode remount-safe).
  const didNavigateRef = useRef(false);

  useEffect(() => {
    if (!isValidCheckoutId(checkoutId)) {
      logWarn("handshake", "Invalid or missing checkoutId in handshake URL", {
        checkoutId,
      });
      window.location.replace(`${PLAN_SELECTION_BASENAME}/not-found`);
      return;
    }

    // Seed sessionStorage + the ?checkoutId= query so the rest of the app (and
    // Auth's post-login redirect_url) share the same id whichever form we
    // entered on.
    seedCheckoutId(checkoutId);

    if (didNavigateRef.current) return;
    didNavigateRef.current = true;

    // Emit and leave in the same tick. We deliberately do not wait for the
    // event POST: every transport inside emitCheckoutHandshake (NR beacon,
    // River queue, Kafka /api/v1/events) issues its request synchronously
    // with `keepalive: true` before the first await, so the browser completes
    // them across the navigation below. Waiting on the response only added
    // the POST's round trip (or a 3s timeout when it failed) to every
    // customer's handshake. In-store native handshakes skip Tamara Auth and go
    // straight to checkout; every other entry routes through Auth.
    void emitCheckoutHandshake(checkoutId, collectHandshakeMetadata());
    navigateAfterHandshake(checkoutId);
  }, [checkoutId]);

  return (
    <div data-testid="handshake-mounted">
      <WelcomeScreen />
    </div>
  );
}
